import React from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '@/components/app';
import { PricingVariant } from '../../../api/pricingVariants';
import { CloneFormData } from '../types';

interface ClonePlansDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  variant: PricingVariant | null;
  variants: PricingVariant[];
  formData: CloneFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<CloneFormData>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

const BASE = 'base';

export function ClonePlansDialog({
  isOpen,
  onOpenChange,
  variant,
  variants,
  formData,
  onFormDataChange,
  onSubmit,
  isSubmitting,
}: ClonePlansDialogProps) {
  const sources = variants.filter((v) => v.id !== variant?.id && (v.plans?.length ?? 0) > 0);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg gap-0 p-0">
        <DialogHeader bordered>
          <DialogTitle>Скопировать тарифы в «{variant?.name}»</DialogTitle>
          <DialogDescription>
            Копия прайс-листа с пересчитанными ценами. Цены потом можно поправить поштучно на странице «Тарифы».
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <FormField label="Источник">
            {(p) => (
              <Select
                value={formData.from_variant_id || BASE}
                onValueChange={(value) =>
                  onFormDataChange((prev) => ({ ...prev, from_variant_id: value === BASE ? undefined : value }))
                }
              >
                <SelectTrigger {...p}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={BASE}>Базовый прайс-лист (текущие цены)</SelectItem>
                  {sources.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>

          <FormField
            label="Суффикс кода"
            required
            hint="Коды тарифов уникальны на всю базу: PREMIUM_1M + _CHEAP → PREMIUM_1M_CHEAP."
          >
            <Input
              value={formData.code_suffix}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, code_suffix: e.target.value.toUpperCase() }))}
              placeholder="_CHEAP"
              className="font-mono text-[13px]"
            />
          </FormField>

          <FormField
            label="Множитель цены"
            hint="0.4 — вдвое с лишним дешевле, 2 — вдвое дороже. Разброс должен быть кратным: на выборке в сотни человек 10% разницы утонут в шуме."
          >
            <Input
              type="number"
              step="0.05"
              min={0.01}
              value={formData.price_multiplier}
              onChange={(e) =>
                onFormDataChange((prev) => ({ ...prev, price_multiplier: parseFloat(e.target.value) || 1 }))
              }
              className="tabular-nums"
            />
          </FormField>
        </DialogBody>

        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            Скопировать
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
